import React, { useState, useEffect } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box } from '@mui/material';
import { setAnimeStatus } from '../services/animeApi';
import ConfirmDialog from './dialogs/ConfirmDialog';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import PopoverMenu from './common/PopoverMenu';
import { useUser } from '../contexts/UserContext';
import { isEarlyAccess } from '../utils/subscription';

interface AnimeStatusButtonProps {
  malId: number;
  initialStatus: number | null;
  onStatusChange: (newStatus: number | null) => void;
}

const AnimeStatusButton: React.FC<AnimeStatusButtonProps> = ({
  malId,
  initialStatus,
  onStatusChange
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [status, setStatus] = useState<number | null>(initialStatus);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const navigate = useNavigate();
  const { user } = useUser();
  const hasEarlyAccess = isEarlyAccess(user?.subscription_plan ?? 0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
  }, []);

  useEffect(() => {
    setStatus(initialStatus);
  }, [initialStatus]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStatusChange = async (newStatus: number | null) => {
    if (!isLoggedIn) {
      navigate('/login');
      return;
    }

    if (newStatus === null) {
      setOpenDeleteConfirmDialog(true);
      return;
    }

    try {
      await setAnimeStatus(malId, newStatus);
      setStatus(newStatus);
      onStatusChange(newStatus);
    } catch (error: any) {
      console.error('アニメステータスの更新に失敗しました', error);
      setErrorMessage(error.message);
      setOpenErrorDialog(true);
    }
    handleClose();
  };

  const handleDeleteConfirm = async () => {
    try {
      await setAnimeStatus(malId, null);
      setStatus(null);
      onStatusChange(null);
    } catch (error) {
      console.error('アニメステータスの削除に失敗しました', error);
    }
    setOpenDeleteConfirmDialog(false);
    handleClose();
  };

  const getStatusIcon = (statusCode: number | null) => {
    switch (statusCode) {
      case 1: return <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'primary.main' }} />;
      case 2: return <PlayCircleOutlineIcon fontSize="small" sx={{ color: 'info.main' }} />;
      case 3: return <PauseCircleOutlineIcon fontSize="small" sx={{ color: 'warning.main' }} />;
      case 4: return <BlockIcon fontSize="small" sx={{ color: '#b99a71' }} />;
      case 5: return <StarBorderIcon fontSize="small" sx={{ color: 'secondary.main' }} />;
      default: return <AddCircleOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />;
    }
  };

  const getStatusText = (statusCode: number | null) => {
    switch (statusCode) {
      case 1: return '視聴済';
      case 2: return '視聴中';
      case 3: return '中断';
      case 4: return '視聴中止';
      case 5: return 'ウォッチリスト';
      default: return 'ステータスを設定';
    }
  };

  const menuOptions = [
    {
      icon: <CheckCircleOutlineIcon />,
      label: '視聴済み',
      onClick: () => handleStatusChange(1),
      iconColor: 'primary.main',
      disabled: !hasEarlyAccess
    },
    {
      icon: <PlayCircleOutlineIcon />,
      label: '視聴中',
      onClick: () => handleStatusChange(2),
      iconColor: 'info.main',
      disabled: !hasEarlyAccess
    },
    {
      icon: <PauseCircleOutlineIcon />,
      label: '中断',
      onClick: () => handleStatusChange(3),
      iconColor: 'warning.main',
      disabled: !hasEarlyAccess
    },
    {
      icon: <BlockIcon />,
      label: '視聴中止',
      onClick: () => handleStatusChange(4),
      iconColor: '#b99a71',
      disabled: !hasEarlyAccess
    },
    {
      icon: <StarBorderIcon />,
      label: 'ウォッチリスト',
      onClick: () => handleStatusChange(5),
      iconColor: 'secondary.main',
      disabled: !hasEarlyAccess
    },
    ...(status !== null ? [{
      icon: <CancelOutlinedIcon />,
      label: '削除',
      onClick: () => handleStatusChange(null),
      iconColor: 'error.main',
      disabled: !hasEarlyAccess
    }] : [])
  ];

  const eaFooter = !hasEarlyAccess ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.75 }}>
      <LockOutlinedIcon sx={{ fontSize: '1rem', color: 'text.disabled' }} />
      <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.3 }}>
        アニメライブラリはアーリーアクセス期間中です
      </Typography>
    </Box>
  ) : undefined;

  return (
    <>
      <Button
        onClick={handleClick}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '12px',
          padding: { xs: '6px 12px', sm: '8px 12px' },
          color: 'text.primary',
          transition: 'all 0.2s ease',
          minWidth: { xs: 'auto', sm: 'auto' },
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: status === null ? 0.75 : 0, sm: 0.75 }, lineHeight: 1 }}>
          {getStatusIcon(status)}
          <Box sx={{ display: { xs: status === null ? 'inline' : 'none', sm: 'inline' } }}>
            {getStatusText(status)}
          </Box>
        </Box>
      </Button>

      <PopoverMenu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        options={menuOptions}
        footer={eaFooter}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      />

      <ConfirmDialog
        open={openDeleteConfirmDialog}
        title="ステータス削除の確認"
        message="ステータスを削除しますか？"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setOpenDeleteConfirmDialog(false)}
        confirmText="削除"
        cancelText="キャンセル"
      />
      <Dialog
        open={openErrorDialog}
        onClose={() => setOpenErrorDialog(false)}
      >
        <DialogTitle>エラー</DialogTitle>
        <DialogContent>
          <Typography>{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenErrorDialog(false)} color="primary">
            閉じる
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AnimeStatusButton;
